import fastdom from 'fastdom';
import { fromEvent, of, Subject, switchMap } from 'rxjs';

/**
 * Carousel file is incharge of create a slider on elements without default size
 * To use thois coursel you need to add the next css properties to your container element
 * overflow-x: auto; to allow scroll event in the container element
 * overflow-y: hidden; to avoid scroll in the y-axis
 * scrollbar-width: none; to avoid show scroll or scroll manually functionality
 */

class Carousel {

  /**
   * Toggles the visibility and disabled state of carousel arrow controls
   * based on the currently visible item indexes.
   *
   * - Arrows are hidden if both the first and last items are visible.
   * - The previous arrow is disabled if the first item is visible.
   * - The next arrow is disabled if the last item is visible.
   * - Otherwise, arrows are visible and enabled as appropriate.
   */
  toggleArrowControllElements() {
    if(this.itemListElement && this.visibleElementIndexes){
      fastdom.measure(() => {
        const lastElementIndex = this.itemListElement.children.length-1;

        const shouldDisabledPreviousArrow = this.visibleElementIndexes.has(0)
        const shouldDisabledNextArrow = this.visibleElementIndexes.has(lastElementIndex)
        const shouldHideArrows = shouldDisabledPreviousArrow && shouldDisabledNextArrow;
          
        fastdom.mutate(() => {
          const visibility = shouldHideArrows ? 'hidden' : 'visible';
          this.previousElement.style.visibility = visibility;
          this.nextElement.style.visibility = visibility;
          this.previousElement.disabled = shouldDisabledPreviousArrow;
          this.nextElement.disabled = shouldDisabledNextArrow;
        });
      });
    }
  }

  /**
   * Set up a subcription event to update controlls based on new visibles elements.
   */
  setUpdateControllsEvent() {
    if (this.visibleElementsSubject$) {
      const updateControllsSubscription = this.visibleElementsSubject$.subscribe(
        this.toggleArrowControllElements.bind(this));
      this.observerSubscriptions.push(updateControllsSubscription);
    }
  }

  /**
   * Smoothly scroll to a specified horizontal position.
   * @param {number} scrollX - The horizontal position to scroll to.
   */
  scrollTo(scrollX) {
    if(this.itemListElement){
      fastdom.mutate(() => {
        this.itemListElement.scroll({
          left: scrollX,
          behavior: 'smooth'
        });
      });
    }
  }

  /**
   * Smoothly scroll by an specific number of pixel.
   * Use this function when no elements are visible in the intersection observer.
   * @param {number} scrollX - The horizontal count of pixels to scroll.
   */
  scrollBy(scrollX){
    if(this.itemListElement){
      fastdom.mutate(() => {
        this.itemListElement.scrollBy({
          left: scrollX,
          behavior: 'smooth'
        });
      });
    }    
  }

  /**
   * Set up the observer for previous button clicks.
   */
  setPreviousArrowObserver() {
    if(this.previousElement){
      const previousButtonClick$ = fromEvent(this.previousElement, 'click').pipe(
        switchMap(() => {
          const position = this.getVisibleElementIndexes().previous;
          const elementToScroll = this.itemListElement.children[position];
          return of(elementToScroll);
        })
      );
  
      const previousButtonSubscription = previousButtonClick$.subscribe((elementToScroll) => {
        fastdom.measure(() => {
          if(elementToScroll){
            const elementRightPosition = elementToScroll.getBoundingClientRect().right;
            const itemListElementRightPosition = this.itemListElement.getBoundingClientRect().right;
            const itemListElementScrollLeft = this.itemListElement.scrollLeft;
            const scrollX = elementRightPosition - itemListElementRightPosition + itemListElementScrollLeft;
            this.scrollTo(scrollX);
          }else{
            this.scrollBy(-100);
          }
        });
      });

      this.observerSubscriptions.push(previousButtonSubscription);
    }
  }

  /**
   * Set up the observer for next button clicks.
   */
  setNextArrowObserver() {
    if(this.nextElement){
      const nextButtonClick$ = fromEvent(this.nextElement, 'click').pipe(
        switchMap(() => {
          const position = this.getVisibleElementIndexes().next;
          const elementToScroll = this.itemListElement.children[position];
          return of(elementToScroll);
        })
      );
  
      const nextButtonSubscription = nextButtonClick$.subscribe((elementToScroll) => {
        fastdom.measure(() => {
          if(elementToScroll){
            const elementLeftPosition = elementToScroll.getBoundingClientRect().left;
            const itemListElementLeftPosition = this.itemListElement.getBoundingClientRect().left;
            const itemListElementScrollLeft = this.itemListElement.scrollLeft;
            const scrollX = elementLeftPosition - itemListElementLeftPosition + itemListElementScrollLeft;
            this.scrollTo(scrollX);
          }else{
            this.scrollBy(100);
          }
        });
      });

      this.observerSubscriptions.push(nextButtonSubscription);
    }
  }

  /**
   * Set the indexes of visible elements based on their positions.
   */
  setIntersectionObserver() {
    if(this.itemListElement && this.visibleElementIndexes){
      const elementsToObserve = Array.from(this.itemListElement.children);
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.visibleElementIndexes.add(entry.target.index);
            } else {
              this.visibleElementIndexes.delete(entry.target.index);
            }
          });

          this.visibleElementsSubject$.next();
        },
        {
          root: this.itemListElement,
          threshold: 0.95,
        }
      );
  
      elementsToObserve.forEach((element, index) => {
        element.index = index;
        this.intersectionObserver.observe(element);
      });
    }
  }

  /**
   * Get the index of the possible elements to scroll on next and previous controlls.
   */
  getVisibleElementIndexes(){
    const indexes = { previous: -1, next: -1 };
    const visibleElementsCount = this.visibleElementIndexes.size;
    const itemListElementCount = this.itemListElement.children.length;

    if(visibleElementsCount > 0 && itemListElementCount > 0) {
      const visibleIndexesArray = [...this.visibleElementIndexes];
      const lastPossibleElementIndex = itemListElementCount - 1;

      // get left and right visible element index in the contairner
      const visibleLeftmostIndex = Math.min(...visibleIndexesArray);
      const visibleRightmostIndex = Math.max(...visibleIndexesArray);

      // get possible index to show in the container
      const possiblePreviousIndex = visibleLeftmostIndex - 1;
      const possibleNextIndex = visibleRightmostIndex + 1;

      indexes.previous = possiblePreviousIndex >= 0 ? possiblePreviousIndex : 0;
      indexes.next = possibleNextIndex <= lastPossibleElementIndex ? possibleNextIndex : lastPossibleElementIndex;
    }

    return indexes;
  }

  /**
   * Method in charge of disconnect the current observer to create a new one
   * Use this method only when you refresh the item list element.
   */
  updateIntersectionObserver(){
    this.intersectionObserver.disconnect();
    this.visibleElementIndexes = new Set();
    this.setIntersectionObserver();
  }

  /**
   * Set the carousel settings from the provided configuration object.
   * @param {Object} settings - The configuration settings for the carousel.
   * @param {string} settings.itemListQuerySelector - The selector for the item list element.
   * @param {string} settings.previousQuerySelector - The selector for the previous button element.
   * @param {string} settings.nextQuerySelector - The selector for the next button element.
   */
  setSettings(settings) {
    this.itemListElement = settings.itemListQuerySelector
      ? document.querySelector(settings.itemListQuerySelector) : undefined;
    this.previousElement = settings.previousQuerySelector
      ? document.querySelector(settings.previousQuerySelector) : undefined;
    this.nextElement = settings.nextQuerySelector
      ? document.querySelector(settings.nextQuerySelector) : undefined;
    this.visibleElementIndexes = new Set();
    this.observerSubscriptions = [];
    this.visibleElementsSubject$ = new Subject();
  }

  /**
   * delete all reference of observers and events from memory
   */
  destroy(){
    this.intersectionObserver.disconnect();
    this.observerSubscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Initialize the carousel and set up observers.
   */
  create() {
    this.setUpdateControllsEvent();
    this.setIntersectionObserver();
    this.setPreviousArrowObserver();
    this.setNextArrowObserver();
  }

  /**
   * Refresh the carousel, updating the positions and visibility of elements.
   */
  refresh() {
    this.scrollTo(0);
    this.updateIntersectionObserver();
  }
}

export default Carousel;